import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Clock, Calendar, Tag, CheckCircle, Link2, Wrench, CalendarCheck, BarChart3, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../../sections/Navbar';
import Footer from '../../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const Article3Page = () => {
  const articleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current?.querySelectorAll('.animate-section') || [], {
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.1,
        ease: 'power2.out',
      });
    }, articleRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={articleRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <article ref={contentRef} className="relative z-10 pt-24 pb-16">
        {/* Hero Image */}
        <div className="animate-section relative h-[50vh] min-h-[400px] overflow-hidden">
          <img 
            src="/blog/article-3-hero.jpg" 
            alt="SEO Checklist" 
            className="w-full h-full object-cover opacity-90"
            style={{ filter: 'hue-rotate(180deg) saturate(0.8)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-sky-50 via-transparent to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 lg:px-8 pb-12">
            <div className="max-w-4xl mx-auto">
              <Link to="/blog">
                <Button
                  variant="ghost"
                  className="mb-4 text-slate-600 hover:text-slate-800 -ml-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět na blog
                </Button>
              </Link>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-[#14b8a6]/20 text-[#14b8a6]">
                  <Tag className="w-3 h-3" />
                  Průvodce
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  6. února 2026
                </span>
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <Clock className="w-4 h-4" />
                  10 min čtení
                </span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-800">
                AI SEO bez chaosu: <span className="text-gradient">7 priorit pro první měsíc</span>
              </h1>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <div className="animate-section">
                <p className="text-slate-600 leading-relaxed text-xl">
                  AI SEO se dá dělat roky a nikam se neposunout. Nebo se dá udělat pár kroků ve správném pořadí a rychle zjistit, co funguje. Tento plán je pro weby, které chtějí být vidět v klasickém SEO i v AI odpovědích.
                </p>
              </div>

              <div className="animate-section my-10 glass-card rounded-xl p-6 border-l-4 border-[#14b8a6]">
                <h3 className="text-xl font-bold mb-4 text-slate-800">Shrnutí plánu</h3>
                <ol className="space-y-3">
                  {[
                    'Vyberte dotazy, které přinášejí poptávky',
                    'Upravte klíčové stránky do citatelné struktury',
                    'Až pak škálujte obsah'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <span className="w-6 h-6 rounded-full bg-[#14b8a6]/20 text-[#14b8a6] flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <span className="w-10 h-10 rounded-full bg-[#0ea5e9]/20 text-[#0ea5e9] flex items-center justify-center text-lg font-bold">1</span>
                    Vyberte 10 nejdůležitějších dotazů
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Použijte dotazy z reálných poptávek a emailů. Zaměřte se na výběr a rozhodování — srovnání, postupy, na co si dát pozor. Tady je největší potenciál.
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <span className="w-10 h-10 rounded-full bg-[#00d4aa]/20 text-[#00d4aa] flex items-center justify-center text-lg font-bold">2</span>
                    Vytvořte 3 klíčové stránky
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Jedna stránka = jedno téma. Struktura: odpověď → kritéria → kroky. Na stránce by mělo být:
                  </p>
                  
                  <ul className="space-y-2 mt-4">
                    {['co to je a pro koho', 'podle čeho se rozhodnout', 'jaký je postup', 'nejčastější otázky'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00d4aa]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <CheckCircle className="w-8 h-8 text-[#14b8a6]" />
                    <span className="w-10 h-10 rounded-full bg-[#14b8a6]/20 text-[#14b8a6] flex items-center justify-center text-lg font-bold">3</span>
                    Ověřte konzistenci
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Zkontrolujte, že se vám nerozcházejí informace napříč webem. U AI odpovědí je rozpor signál nízké důvěryhodnosti. Konzistence je levná a má velký dopad.
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <Link2 className="w-8 h-8 text-[#22c55e]" />
                    <span className="w-10 h-10 rounded-full bg-[#22c55e]/20 text-[#22c55e] flex items-center justify-center text-lg font-bold">4</span>
                    Prolinkujte obsah
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Každý článek by měl mít vztah k dalšímu:
                  </p>
                  
                  <ul className="space-y-2 mt-4">
                    {['2–3 odkazy na související články', '1 odkaz na službu', '1 odkaz na hlavní téma'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <Wrench className="w-8 h-8 text-[#0ea5e9]" />
                    <span className="w-10 h-10 rounded-full bg-[#0ea5e9]/20 text-[#0ea5e9] flex items-center justify-center text-lg font-bold">5</span>
                    Zkontrolujte techniku
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Indexace, rychlost, mobilní verze, základní structured data. Bez toho je škálování drahé a neefektivní.
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <CalendarCheck className="w-8 h-8 text-[#00d4aa]" />
                    <span className="w-10 h-10 rounded-full bg-[#00d4aa]/20 text-[#00d4aa] flex items-center justify-center text-lg font-bold">6</span>
                    Publikujte pravidelně
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Lepší je 1 kvalitní článek týdně než 10 textů, které se překrývají. V prvním měsíci vyhrajete hlavně pořádkem.
                  </p>
                </div>
              </div>

              <div className="animate-section my-12">
                <div className="glass-card rounded-xl p-6">
                  <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-800">
                    <BarChart3 className="w-8 h-8 text-[#14b8a6]" />
                    <span className="w-10 h-10 rounded-full bg-[#14b8a6]/20 text-[#14b8a6] flex items-center justify-center text-lg font-bold">7</span>
                    Sledujte výsledky
                  </h2>
                  
                  <p className="text-slate-600 leading-relaxed">
                    Dívejte se na konkrétní stránky:
                  </p>
                  
                  <ul className="space-y-2 mt-4">
                    {['Které stránky přivádějí návštěvy?', 'Které vedou k poptávkám?', 'Kde lidé odcházejí?'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#14b8a6]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CTA */}
              <div className="animate-section my-12">
                <div className="glass-card rounded-2xl p-8 text-center border border-[#14b8a6]/30">
                  <h3 className="text-2xl font-bold mb-4 text-slate-800">Chcete jasnou mapu priorit?</h3>
                  <p className="text-slate-500 mb-6">
                    Zjistěte, co má smysl řešit jako první na vašem webu.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/audit">
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                      >
                        AI Visibility Audit
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              <div className="animate-section my-12">
                <h3 className="text-xl font-bold mb-6 text-slate-800">Další čtení</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Link
                    to="/blog/ai-meni-vysledky-vyhledavani"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#0ea5e9]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#0ea5e9] mb-2">AI SEO</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">AI mění výsledky vyhledávání</div>
                  </Link>
                  <Link
                    to="/blog/proc-lide-budou-vic-vyuzivat-ai-vyhledavani"
                    className="glass-card rounded-xl p-5 text-left hover:border-[#00d4aa]/50 transition-colors group"
                  >
                    <div className="text-sm text-[#00d4aa] mb-2">Trendy</div>
                    <div className="font-semibold text-slate-800 group-hover:text-gradient transition-colors">Proč poroste AI vyhledávání</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default Article3Page;
